import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { trackUserSignup, trackUserLogin } from "../lib/analytics";
import { supabase } from "../lib/supabase";

const AuthScreen = ({ onLogin, onSignup }) => {
  const { signInWithProvider } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistStatus, setWaitlistStatus] = useState(null); // "success" | "error" | null
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setLoading(true);

    try {
      if (isLogin) {
        await onLogin(email, password);
        trackUserLogin("email");
      } else {
        await onSignup(email, password);
        trackUserSignup("email");
        setNotice("Check your email to confirm your account before signing in.");
      }
    } catch (err) {
      const fallback = "Authentication failed. Please try again.";
      const message = err?.message || fallback;
      if (isLogin && message.toLowerCase().includes("invalid login credentials")) {
        setError("Invalid login credentials. If you just signed up, confirm your email first.");
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleWaitlist = async (e) => {
    e.preventDefault();
    setWaitlistLoading(true);
    setWaitlistStatus(null);
    const { error } = await supabase.from("waitlist").insert({ email: waitlistEmail });
    if (error) {
      setWaitlistStatus(error.code === "23505" ? "duplicate" : "error");
    } else {
      setWaitlistStatus("success");
      setWaitlistEmail("");
    }
    setWaitlistLoading(false);
  };

  const handleOAuthSignIn = async (provider) => {
    setError("");
    setNotice("");
    setLoading(true);

    try {
      await signInWithProvider(provider);
      // Track as login (OAuth doesn't distinguish sign-up vs login in Supabase)
      trackUserLogin(provider);
    } catch (err) {
      setError(err.message || `Failed to sign in with ${provider}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mb-4">
            <img
              src="/logo.png"
              alt="BioBlobs Logo"
              className="w-24 h-24 mx-auto"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">BioBlobs</h1>
          <p className="text-gray-600">Your journey to medical mastery</p>
        </div>

        {notice && (
          <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <p className="text-sm text-emerald-700">{notice}</p>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="student@medschool.com"
              required
              disabled={loading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="••••••••"
              required
              disabled={loading}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-violet-500 to bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Please wait..." : isLogin ? "Sign In" : "Create Account"}
          </button>
        </form>

        <div className="my-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with</span>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <button
            onClick={() => handleOAuthSignIn("google")}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span className="text-gray-700 font-medium">Sign in with Google</span>
          </button>

          <button
            onClick={() => handleOAuthSignIn("github")}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path
                fillRule="evenodd"
                d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-gray-700 font-medium">Sign in with GitHub</span>
          </button>
        </div>

        <div className="mt-4 text-center">
          <button
            onClick={() => setIsLogin(!isLogin)}
            className="text-purple-800 hover:text-black text-sm font-medium"
          >
            {isLogin
              ? "Don't have an account? Sign up"
              : "Already have an account? Sign in"}
          </button>
        </div>

        <div className="mt-6 rounded-xl bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-200 p-4">
          <div>
            <p className="font-bold text-teal-800 text-sm mb-0.5">Get notified at launch 🔔</p>
            <p className="text-xs text-teal-700 mb-2">Still in beta — enter your email to be the first to know.</p>
            <form onSubmit={handleWaitlist} className="flex gap-1.5">
              <input
                type="email"
                value={waitlistEmail}
                onChange={(e) => setWaitlistEmail(e.target.value)}
                placeholder="your@email.com"
                required
                disabled={waitlistLoading || waitlistStatus === "success"}
                className="flex-1 px-2.5 py-1.5 text-xs border border-teal-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white min-w-0"
              />
              <button
                type="submit"
                disabled={waitlistLoading || waitlistStatus === "success"}
                className="px-3 py-1.5 text-xs bg-teal-500 text-white rounded-lg font-semibold hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {waitlistLoading ? "..." : "Join"}
              </button>
            </form>
            {waitlistStatus === "success" && (
              <p className="mt-1.5 text-xs font-medium text-teal-700">✅ You're on the list!</p>
            )}
            {waitlistStatus === "duplicate" && (
              <p className="mt-1.5 text-xs text-teal-600">You're already signed up!</p>
            )}
            {waitlistStatus === "error" && (
              <p className="mt-1.5 text-xs text-red-500">Something went wrong. Please try again.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthScreen;
